import { useEffect, useRef, useState } from "react";
import {
  mountViewer, type POI as ViewerPOI, type Pin as ViewerPin, type Frame as ViewerFrame,
  type ViewerHandle, type TileLayerSet,
} from "@viewer";
import type { FlightMeta, PanoramaWithUrl, PanoView } from "../api/types";
import type { AnnotationStore } from "../state/AnnotationStore";
import { useApiClient } from "../api/ApiClientContext";
import { setPlaying } from "../presence/playing";
import type { OpenPanorama } from "../pages/panoParams";
import type { FeatureCollection, Geometry } from "geojson";

export interface ViewerProps {
  videoUrl: string;
  trackUrl: string;
  /** HLS master manifest URL — when set the viewer attaches hls.js. */
  manifestUrl?: string;
  /** 320p scrub-preview video URL — used for low-latency drag-scrubbing. */
  previewUrl?: string;
  /** Team JWT, attached by the viewer to Worker-origin manifest fetches. */
  authToken: string;
  /** The Worker base URL the auth header is scoped to. */
  workerOrigin: string;
  meta: FlightMeta;
  store: AnnotationStore;
  reviewer: { reviewerId: string; displayName: string; role: "admin" | "editor" | "viewer" };
  onFrameCapture: (
    frameId: string,
    fullBlob: Blob,
    thumbBlob: Blob,
    meta: {
      videoTimeS: number;
      utc: number;
      lat: number;
      lon: number;
      poiId?: string;
      source?: "video" | "panorama";
      panoramaId?: string;
      panoView?: PanoView;
    },
  ) => Promise<void>;
  onOpenFrames: () => void;
  onViewFrame: (frameId: string) => void;
  /** When provided, the viewer seeks the video to this time (seconds) on mount
   *  and whenever this value changes. Used by ?t= deep-links from the Saved
   *  Frames gallery. */
  seekToTime?: number;
  /** Admin cross-team viewing: forwarded as ?team= on the panoramas fetch. */
  team?: string;
  /** Parsed client reference overlays (from ViewerPage). */
  overlays: { id: string; name: string; fc: FeatureCollection<Geometry | null> }[];
  /** Which overlay ids are currently visible. */
  visibleIds: Set<string>;
  /** When set, opens this panorama at the given PSV view once the viewer and
   *  its panorama list are ready. Used by ?pano= deep-links. */
  openPanorama?: OpenPanorama;
  /** Presigned URL for the flight's footprint sidecar. When set, the viewer
   *  renders the ground-projection overlay. */
  footprintUrl?: string;
  /** Sibling flight tracks to render greyed-out (Plan 13). */
  ghostTracks?: { flightId: string; name: string; latlngs: [number, number][] }[];
  onGhostTrackClick?: (flightId: string) => void;
  onVideoEnded?: () => void;
  /** Reports playback state (Plan 20): true on play, false on pause/ended.
   *  Feeds the seat-presence heartbeat, which counts watching as activity. */
  onPlayStateChange?: (playing: boolean) => void;
  /** Receives the mounted viewer handle, and null when it is disposed. Lets
   *  the page reach `handle.pause()` for the guided tour. */
  onHandleReady?: (handle: ViewerHandle | null) => void;
  /** Basemap tile layers (web/src/map/basemaps.ts): the keyed Esri set
   *  online, the NSW set for a flight playing from the offline cache.
   *  Unset = the shared viewer's NSW defaults. Read at mount only. */
  tileLayers?: TileLayerSet;
  /** Called once per basemap tile the map starts fetching. The page passes the
   *  tile meter's counter only for the (paid) Esri set. Read at mount only. */
  onTileLoad?: () => void;
}

export function Viewer({
  videoUrl, trackUrl, manifestUrl, previewUrl, authToken, workerOrigin,
  meta, store, reviewer, onFrameCapture, onOpenFrames, onViewFrame, seekToTime, team, openPanorama,
  footprintUrl, overlays, visibleIds, ghostTracks, onGhostTrackClick, onVideoEnded, onHandleReady,
  tileLayers, onTileLoad, onPlayStateChange,
}: ViewerProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<ViewerHandle | null>(null);
  /** What the mounted viewer holds, by id → FeatureCollection; null until mounted. */
  const mountedOverlaysRef = useRef<Map<string, FeatureCollection<Geometry | null>> | null>(null);
  const openedPanoRef = useRef(false);
  const apiClient = useApiClient();
  const [panoramas, setPanoramas] = useState<PanoramaWithUrl[]>([]);
  // The viewer is mounted exactly once, only after the panoramas fetch has
  // settled — `mountViewer` takes `initialPanoramas` at call time, so mounting
  // before the fetch resolves would force a full teardown + rebuild when the
  // list arrives. That double-mount orphaned deep-link actions and leaked the
  // first viewer's async setup onto the second. Gate the mount on this flag.
  // `panoramas` and `overlays` are intentionally read at mount time and are NOT
  // in the mount effect's dependency array — they are stable by the time the
  // `panoramasReady` gate opens, and adding them would cause a teardown+rebuild
  // on every subsequent re-render. Do not add them as deps.
  const [panoramasReady, setPanoramasReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPanoramasReady(false);
    apiClient
      .getPanoramas(meta.flightId, team !== undefined ? { team } : {})
      .then((resp) => {
        if (cancelled) return;
        setPanoramas(resp?.panoramas ?? []);
        setPanoramasReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("getPanoramas failed", err);
        setPanoramasReady(true); // mount anyway, with no panoramas
      });
    return () => { cancelled = true; };
  }, [apiClient, meta.flightId, team]);

  useEffect(() => {
    if (!panoramasReady) return;
    if (ref.current === null) return;
    const initial = store.snapshot().annotations;
    const handle = mountViewer(ref.current, {
      videoSrc: videoUrl,
      videoCrossOrigin: "anonymous",
      allowLocalFallback: false,
      trackSrc: trackUrl,
      hlsSrc: manifestUrl,
      previewSrc: previewUrl,
      authToken,
      workerOrigin,
      footprintUrl,
      ...(tileLayers !== undefined ? { tileLayers } : {}),
      ...(onTileLoad !== undefined ? { onTileLoad } : {}),
      ...(onPlayStateChange !== undefined ? { onPlayStateChange } : {}),
      meta: meta as unknown,
      currentReviewer: reviewer,
      initialPois: initial.pois as unknown as ViewerPOI[],
      initialPins: initial.pins as unknown as ViewerPin[],
      initialFrames: initial.frames as unknown as ViewerFrame[],
      initialPanoramas: panoramas,
      overlays: overlays.map((o) => ({ id: o.id, fc: o.fc })),
      onPoiCreate: async (poi) => store.upsertPoi(poi as unknown as Parameters<typeof store.upsertPoi>[0]),
      onPoiUpdate: async (poi) => store.upsertPoi(poi as unknown as Parameters<typeof store.upsertPoi>[0]),
      onPoiDelete: async (id) => store.deletePoi(id),
      onPinCreate: async (pin) => store.upsertPin(pin as unknown as Parameters<typeof store.upsertPin>[0]),
      onPinUpdate: async (pin) => store.upsertPin(pin as unknown as Parameters<typeof store.upsertPin>[0]),
      onPinDelete: async (id) => store.deletePin(id),
      onFrameCapture,
      onOpenFrames,
      onViewFrame,
      onGhostTrackClick,
      onVideoEnded,
    });
    handleRef.current = handle;
    mountedOverlaysRef.current = new Map(overlays.map((o) => [o.id, o.fc]));
    onHandleReady?.(handle);
    const unsub = store.subscribe(() => {
      const s = store.snapshot().annotations;
      handle.setPois(s.pois as unknown as ViewerPOI[]);
      handle.setPins(s.pins as unknown as ViewerPin[]);
      handle.setFrames(s.frames as unknown as ViewerFrame[]);
    });
    return () => {
      unsub();
      handle.dispose();
      // Tearing the <video> down never fires `pause`, so the presence store
      // would otherwise keep reporting a flight that is no longer playing.
      setPlaying(false);
      handleRef.current = null;
      mountedOverlaysRef.current = null;
      onHandleReady?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoUrl, trackUrl, manifestUrl, previewUrl, authToken, workerOrigin, footprintUrl, meta,
    panoramasReady,
  ]);

  // Overlays added or removed from the website while the flight is open
  // (spec 2026-09-09). The mount-time list went in through mountViewer; this
  // effect only reconciles changes after that, keyed by id and by the
  // FeatureCollection's identity so a replaced overlay is rebuilt. It runs
  // before the visibility effect below so a new layer exists when that one
  // switches it on.
  useEffect(() => {
    if (!panoramasReady) return;
    const handle = handleRef.current;
    const mounted = mountedOverlaysRef.current;
    if (handle === null || mounted === null) return;
    const next = new Map(overlays.map((o) => [o.id, o.fc]));
    for (const [id, fc] of next) {
      if (mounted.get(id) === fc) continue;
      handle.addOverlay(id, fc as Parameters<ViewerHandle["addOverlay"]>[1]);
      mounted.set(id, fc);
    }
    for (const id of [...mounted.keys()]) {
      if (next.has(id)) continue;
      handle.removeOverlay(id);
      mounted.delete(id);
    }
  }, [overlays, panoramasReady]);

  useEffect(() => {
    if (!panoramasReady) return;
    const handle = handleRef.current;
    if (handle === null) return;
    // NOTE: the viewer patches its internal `_setOverlayVisibility` late (deep
    // in mountViewer's async setup), so this effect's FIRST run (same commit as
    // mount) is a silent no-op. That's fine only because `visibleIds` starts
    // empty and overlay layers default hidden. If a future change seeds a
    // non-empty initial `visibleIds` (e.g. a deep-link or persisted state),
    // those layers will NOT appear on load — handle that in viewer.js with a
    // pending-stash like `_pendingPanorama`, don't just set initial state here.
    for (const o of overlays) handle.setOverlayVisibility(o.id, visibleIds.has(o.id));
  }, [overlays, visibleIds, panoramasReady]);

  useEffect(() => {
    if (!panoramasReady) return;
    // setGhostTracks stashes internally until the map exists, so unlike the
    // overlay-visibility effect above the first run is never lost.
    handleRef.current?.setGhostTracks(ghostTracks ?? []);
  }, [ghostTracks, panoramasReady]);

  useEffect(() => {
    // Gate on panoramasReady so this re-runs after the (single) mount — the
    // mount is deferred until the panoramas fetch settles, and the handle does
    // not exist before then.
    if (!panoramasReady) return;
    if (typeof seekToTime !== "number" || Number.isNaN(seekToTime)) return;
    handleRef.current?.seekTo(seekToTime);
  }, [seekToTime, panoramasReady]);

  useEffect(() => {
    // A deep-link opens the panorama once, on arrival. The guard makes the
    // effect idempotent so a later re-render (new openPanorama identity, or a
    // panoramas refresh) cannot re-snap the view out from under the user.
    if (openedPanoRef.current) return;
    if (openPanorama === undefined) return;
    const handle = handleRef.current;
    if (handle === null) return;
    if (!panoramas.some((p) => p.id === openPanorama.id)) return;
    handle.openPanorama(openPanorama.id, {
      yaw: openPanorama.yaw,
      pitch: openPanorama.pitch,
      zoom: openPanorama.zoom,
    });
    openedPanoRef.current = true;
  }, [openPanorama, panoramas]);

  return <div ref={ref} style={{ width: "100%", height: "100%" }} />;
}
